import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthGuard } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardList, faCalendarAlt, faHome, faUserCircle, faEnvelope, faPhone, faMoneyBillWave, faCommentDots, faTools, faSignOutAlt, faCircle, faSpinner, faInfoCircle } from "@fortawesome/free-solid-svg-icons";

const ReservationsPage = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userDataString = localStorage.getItem("user");
  let userId = null;

  try {
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      userId = userData.unique;
    }
  } catch (e) {
    console.error("Error parsing user data from localStorage:", e);
    // Handle error, e.g., redirect to login or show an error message
  }

  useEffect(() => {
    const fetchReservations = async () => {
      setLoading(true);
      if (!token || !userId) {
        toast.warn("Authentication details missing. Please log in.");
        setReservations([]);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `https://backend.inyumba.co.ke/iNyumba/user/reservations/viewYour/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        // Ensure response.data.response is an array, or wrap it if it's a single object
        setReservations(Array.isArray(response.data.response) ? response.data.response : (response.data.response ? [response.data.response] : []));
      } catch (error) {
        console.error("Error fetching reservations:", error);
        setReservations([]); // Clear data on error
        if (error.response && error.response.data && error.response.data.response) {
          toast.error(error.response.data.response);
        } else {
          toast.error("Failed to load your reservations. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReservations();
  }, [token, userId]); // Re-run effect if token or userId changes

  const handleCommentClick = (propertyId) => {
    localStorage.setItem("specificPropertyId", propertyId);
    navigate("/user/addcomment");
  };

  const handleMaintenanceClick = (propertyId) => {
    localStorage.setItem("specificPropertyId", propertyId);
    navigate("/user/addmaintenance");
  };

  const handleVacateClick = () => {
    // Note: The original code does not pass propertyId to handleVacateClick.
    // If a propertyId is needed for vacating, it should be retrieved from the reservation object
    // or adjusted in the backend logic. For now, maintaining original functionality.
    navigate("/user/addvacate");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
      case "Paid":
      case "Active": // Assuming 'Active' might also be a green status
        return "bg-green-100 text-green-800";
      case "Pending":
      case "Awaiting Payment":
        return "bg-yellow-100 text-yellow-800";
      case "Rejected":
      case "Cancelled":
      case "Failed":
      case "Vacated": // Assuming 'Vacated' is a distinct final state, maybe red or grey depending on context
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-5xl mx-auto py-8 text-center animate-fade-in-down">
          <div className="flex items-center justify-center mb-6">
            <FontAwesomeIcon icon={faClipboardList} className="text-green-600 text-6xl mr-4" />
            <h1 className="text-5xl font-extrabold text-gray-900">
              Your Current Reservations
            </h1>
          </div>
          <p className="text-gray-600 text-lg mb-6">
            Manage and interact with your currently reserved properties.
          </p>
          <span className="text-gray-700 text-xl font-semibold flex items-center justify-center sm:justify-start mb-10">
            <FontAwesomeIcon icon={faCalendarAlt} className="mr-3 text-2xl text-blue-500" />
            Today's Date: {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </span>

          {loading ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-600 text-xl flex flex-col items-center justify-center">
              <FontAwesomeIcon icon={faSpinner} spin className="text-green-500 text-5xl mb-4" />
              <p>Loading your reservations...</p>
            </div>
          ) : reservations.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-600 text-xl">
              <FontAwesomeIcon icon={faInfoCircle} className="text-blue-400 text-5xl mb-4" />
              <p>You currently have no active reservations.</p>
              <p className="mt-2 text-lg">Time to find your perfect home!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {reservations.map((reservation) => (
                <div
                  key={reservation.room_id} // Assuming room_id is unique per reservation
                  className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 space-y-4 flex flex-col justify-between"
                >
                  <h3 className="text-3xl font-bold text-indigo-700 mb-4 border-b-2 border-green-200 pb-3 flex items-center">
                    <FontAwesomeIcon icon={faHome} className="mr-3 text-2xl" />
                    House: {reservation.house_no}
                  </h3>
                  <div className="space-y-2 text-left">
                    <p className="text-lg">
                      <span className="font-medium text-gray-600">
                        <FontAwesomeIcon icon={faUserCircle} className="mr-2 text-blue-400" />User:
                      </span>{" "}
                      <span className="font-semibold text-gray-800">{reservation.user_name}</span>
                    </p>
                    <p className="text-lg">
                      <span className="font-medium text-gray-600">
                        <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-blue-400" />Email:
                      </span>{" "}
                      <span className="font-semibold text-gray-800">{reservation.email}</span>
                    </p>
                    <p className="text-lg">
                      <span className="font-medium text-gray-600">
                        <FontAwesomeIcon icon={faPhone} className="mr-2 text-blue-400" />Phone:
                      </span>{" "}
                      <span className="font-semibold text-gray-800">{reservation.phone}</span>
                    </p>
                    <p className="text-lg">
                      <span className="font-medium text-gray-600">
                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-blue-400" />Entry Date:
                      </span>{" "}
                      <span className="font-semibold text-gray-800">{new Date(reservation.entrydate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </p>
                    <p className="text-lg">
                      <span className="font-medium text-gray-600">
                        <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2 text-blue-400" />Deposit:
                      </span>{" "}
                      <span className="font-semibold text-green-600">KSh {parseFloat(reservation.deposit_paid).toLocaleString()}</span>
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-3 mt-4 justify-center sm:justify-start">
                    <span className={`inline-flex items-center px-4 py-2 rounded-full text-md font-semibold ${getStatusColor(reservation.booking_status)}`}>
                      <FontAwesomeIcon icon={faCircle} className="mr-2 text-xs" />
                      Booking: {reservation.booking_status}
                    </span>
                    <span className={`inline-flex items-center px-4 py-2 rounded-full text-md font-semibold ${getStatusColor(reservation.payment_status)}`}>
                      <FontAwesomeIcon icon={faCircle} className="mr-2 text-xs" />
                      Payment: {reservation.payment_status}
                    </span>
                  </div>

                  <div className="flex flex-wrap justify-center gap-3 pt-6 border-t border-gray-100 mt-6">
                    {/* Comment Button */}
                    <button
                      onClick={() => handleCommentClick(reservation.property_id)}
                      className="flex items-center space-x-2 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold py-3 px-6 rounded-full text-lg shadow-md transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    >
                      <FontAwesomeIcon icon={faCommentDots} />
                      <span>Comment</span>
                    </button>

                    {/* Maintenance Button */}
                    <button
                      onClick={() => handleMaintenanceClick(reservation.property_id)}
                      className="flex items-center space-x-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-6 rounded-full text-lg shadow-md transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                    >
                      <FontAwesomeIcon icon={faTools} />
                      <span>Maintenance</span>
                    </button>

                    {/* Vacate Button */}
                    <button
                      onClick={handleVacateClick}
                      className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-full text-lg shadow-md transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} />
                      <span>Vacate</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AuthGuard>
  );
};

export default ReservationsPage;